"use client";

import { useState } from "react";
import { Notice, PillGroup, TextField } from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H2 } from "@/components/tool-kit/styles";
import { DataInput } from "@/components/tools/research-design/data-input";
import {
  DistractorPanel,
  ItemTable,
  TestSummary,
} from "@/components/tools/research-design/item-analysis-parts";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { NumberFormatter } from "@/lib/tool-kit/research-design/format";
import {
  analyzeTest,
  expandCompactRows,
  isAnswerCell,
  isScoreCell,
  parseAnswerKey,
  readScores,
  scoreLetters,
} from "@/lib/tool-kit/research-design/item-analysis";
import {
  labelsFor,
  type ParsedTable,
  parseTable,
} from "@/lib/tool-kit/research-design/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const ITEM_ANALYSIS_CALCULATOR_STRINGS = {
  modeLegend: "Your data",
  modeLetters: "Answer letters and a key",
  modeScored: "Already scored 0/1",
  dataLabel: "Answers — one row per student, one column per item",
  dataHint:
    "Paste from a spreadsheet, or type answers separated by commas or spaces. A row typed as one run of letters (BDACAB) is split into one answer per item. Blank cells count as omitted.",
  dataPlaceholder: "Student\tQ1\tQ2\tQ3\nS01\tB\tD\tA\nS02\tB\tC\tA",
  scoredLabel: "Scores — one row per student, one column per item",
  scoredHint:
    "1 = correct, 0 = wrong. Paste from a spreadsheet or type scores separated by commas or spaces; a blank cell is scored as wrong.",
  scoredPlaceholder: "Student\tQ1\tQ2\tQ3\nS01\t1\t1\t0\nS02\t1\t0\t1",
  loadExample: "Load example",
  clear: "Clear",
  exampleLetters:
    "Student\tQ1\tQ2\tQ3\tQ4\tQ5\tQ6\nS01\tB\tD\tC\tC\tA\tB\nS02\tB\tD\tA\tC\tD\tA\nS03\tB\tD\tB\tC\tB\tB\nS04\tB\tD\tC\tC\tA\tB\nS05\tB\tD\tA\tA\tD\tC\nS06\tA\tB\tA\tA\tC\tA\nS07\tB\tD\tA\tC\tD\tB\nS08\tD\tD\tA\tC\tB\tB\nS09\tC\tD\tA\tB\tD\tC\nS10\tB\tD\tA\tB\tC\tA\nS11\tA\tD\tD\tC\tB\tB\nS12\tC\tD\tB\tA\t\tD",
  exampleScored:
    "Student\tQ1\tQ2\tQ3\tQ4\tQ5\tQ6\nS01\t1\t1\t0\t1\t1\t1\nS02\t1\t1\t1\t1\t0\t0\nS03\t1\t1\t0\t1\t0\t1\nS04\t1\t1\t0\t1\t1\t1\nS05\t1\t1\t1\t0\t0\t0\nS06\t0\t0\t1\t0\t0\t0\nS07\t1\t1\t1\t1\t0\t1\nS08\t0\t1\t1\t1\t0\t1\nS09\t0\t1\t1\t0\t0\t0\nS10\t1\t1\t1\t0\t0\t0\nS11\t0\t1\t0\t1\t0\t1\nS12\t0\t1\t0\t0\t0\t0",
  exampleKey: "B D A C A B",
  keyLabel: "Answer key",
  keyPlaceholder: "B D A C A B",
  keyHint:
    "One letter per item, in column order — separated by spaces or commas, or typed as one run.",
  keyRead: "Key read as {key}.",
  keyPrompt: "Enter the answer key to score the letters.",
  keyMismatch:
    "The key has {key} answers but the table has {items} item columns. Items without a key answer are ignored; key answers without a column score as omitted.",
  firstRowHeader: "First row holds item names",
  firstColumnIds: "First column holds student names or IDs",
  showParsed: "Show the table as read",
  previewNote: "Showing the first {shown} of {total} rows.",
  idHeader: "Student",
  readAs: "Read as {students} × {items}.",
  studentCount: "one: {count} student|other: {count} students",
  itemCount: "one: {count} item|other: {count} items",
  itemFallback: "Item {n}",
  groupLegend: "Upper and lower groups",
  group27: "27% (Kelley)",
  group25: "25%",
  group33: "33%",
  group50: "50%",
  results: "Results",
  enterPrompt:
    "Paste at least two students' answers, or load the example, to analyze the items.",
  blankNote:
    "one: {count} blank answer was scored as wrong.|other: {count} blank answers were scored as wrong.",
  invalidNote:
    "one: {count} cell was not 0, 1 or blank, so it was scored as wrong.|other: {count} cells were not 0, 1 or blank, so they were scored as wrong.",
  students: "Students",
  items: "Items",
  meanScore: "Mean score",
  sdScore: "SD (σ)",
  kr20: "KR-20",
  sem: "SEM",
  kr20Working:
    "KR-20 = k ÷ (k − 1) × (1 − Σpq ÷ σ²) = {k} ÷ {k1} × (1 − {sumPq} ÷ {variance}) = {kr20}",
  kr20Note:
    "σ² is the variance of total scores divided by N, which matches the pq item variances and makes KR-20 equal to Cronbach's alpha. SEM = σ × √(1 − KR-20).",
  colItem: "Item",
  colKey: "Key",
  colP: "p",
  colUpper: "Upper p",
  colLower: "Lower p",
  colD: "D",
  colR: "r (corrected)",
  colFlags: "Flags",
  flagVeryEasy: "Very easy",
  flagVeryHard: "Very hard",
  flagWeak: "Weak discrimination",
  flagNegative: "Negative — check the key",
  groupsNote:
    "Upper and lower groups: the top and bottom {share} of students by total score, {size} in each. Students tied at a cut-off are split by row order.",
  flagsNote:
    "Flags: very easy above p = 0.90; very hard below p = 0.20; weak when D is 0 to 0.19 (Ebel's poor band); negative when D or r is below zero. r is the corrected point-biserial — the item against the total of the other items.",
  downloadCsv: "Download item statistics (CSV)",
  csvFile: "item-analysis.csv",
  distractorHeading: "Distractor analysis",
  distractorItem: "Item to inspect",
  colOption: "Option",
  colAll: "All students",
  colUpperCount: "Upper group",
  colLowerCount: "Lower group",
  colNote: "Note",
  keyMark: "{option} (key)",
  optionOmitted: "Blank",
  optionOther: "Other answers",
  countShare: "{count} ({share})",
  distractorNever: "Chosen by nobody",
  distractorRare: "Chosen by fewer than 5%",
  distractorUpper: "Draws more upper than lower students",
  reviewHeading: "Distractors to review",
  reviewLine: "{item}, option {option}: {note}",
  reviewNone: "No distractor problems found.",
  distractorNote:
    "A working distractor is chosen by at least 5% of students, and by more of the lower group than the upper group (Haladyna & Downing, 1993).",
} as const;

export type ItemAnalysisStrings = typeof ITEM_ANALYSIS_CALCULATOR_STRINGS;
type Mode = "letters" | "scored";
type Share = "0.27" | "0.25" | "0.33" | "0.5";

function prepare(
  raw: string,
  mode: Mode,
  key: readonly string[],
  table: ParsedTable
) {
  if (mode === "scored") {
    const width = Math.max(0, ...table.body.map((row) => row.length));
    const read = readScores(table.body, width);
    return {
      body: table.body,
      scores: read.scores,
      responses: null,
      blank: read.blank,
      invalid: read.invalid,
      width,
      expanded: false,
    };
  }
  const compact = expandCompactRows(table.body, key.length);
  const width = Math.max(0, ...compact.body.map((row) => row.length));
  const scored =
    key.length === 0 || raw.trim() === ""
      ? null
      : scoreLetters(compact.body, key);
  return {
    body: compact.body,
    scores: scored?.scores ?? [],
    responses: scored?.responses ?? null,
    blank: scored?.blank ?? 0,
    invalid: 0,
    width,
    expanded: compact.expanded,
  };
}

function KeyField({
  value,
  onChange,
  itemKey,
  strings,
}: {
  value: string;
  onChange: (value: string) => void;
  itemKey: readonly string[];
  strings: ItemAnalysisStrings;
}) {
  return (
    <div className="space-y-2">
      <TextField
        hint={strings.keyHint}
        id="ia-key"
        label={strings.keyLabel}
        mono
        onChange={onChange}
        placeholder={strings.keyPlaceholder}
        value={value}
      />
      {itemKey.length > 0 ? (
        <p className={HINT}>{t(strings.keyRead, { key: itemKey.join(" ") })}</p>
      ) : null}
    </div>
  );
}

function ScoringNotes({
  blank,
  invalid,
  strings,
  fmt,
}: {
  blank: number;
  invalid: number;
  strings: ItemAnalysisStrings;
  fmt: NumberFormatter;
}) {
  const locale = useToolLocale();
  return (
    <>
      {blank > 0 ? (
        <Notice>
          {tPlural(strings.blankNote, locale, blank, { count: fmt(blank) })}
        </Notice>
      ) : null}
      {invalid > 0 ? (
        <Notice>
          {tPlural(strings.invalidNote, locale, invalid, {
            count: fmt(invalid),
          })}
        </Notice>
      ) : null}
    </>
  );
}

export default function ItemAnalysisCalculatorWidget() {
  const strings = useToolUi(ITEM_ANALYSIS_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("letters");
  const [raw, setRaw] = useState("");
  const [keyText, setKeyText] = useState("");
  const [header, setHeader] = useState<boolean | null>(null);
  const [ids, setIds] = useState<boolean | null>(null);
  const [share, setShare] = useState<Share>("0.27");

  const key = mode === "letters" ? parseAnswerKey(keyText) : [];
  const isValue =
    mode === "letters"
      ? (cell: string) => isAnswerCell(cell, key.length)
      : isScoreCell;
  const table = parseTable(raw, isValue, {
    header: header ?? undefined,
    ids: ids ?? undefined,
  });
  const data = prepare(raw, mode, key, table);
  const itemTotal = mode === "letters" ? key.length : data.width;
  const names = labelsFor(
    data.expanded ? null : table.columns,
    Math.max(itemTotal, data.width),
    (n) => t(strings.itemFallback, { n })
  );
  const analysis = analyzeTest(data.scores, Number(share));
  const needsKey = mode === "letters" && key.length === 0 && raw.trim() !== "";
  const mismatch =
    mode === "letters" &&
    key.length > 0 &&
    data.width > 0 &&
    key.length !== data.width;
  const inputStrings =
    mode === "letters"
      ? strings
      : {
          ...strings,
          dataLabel: strings.scoredLabel,
          dataHint: strings.scoredHint,
          dataPlaceholder: strings.scoredPlaceholder,
        };

  function reset(text: string) {
    setRaw(text);
    setHeader(null);
    setIds(null);
    if (mode === "letters") {
      setKeyText(text === "" ? "" : strings.exampleKey);
    }
  }

  return (
    <>
      <PillGroup
        label={strings.modeLegend}
        name="ia-mode"
        onChange={(next) => {
          setMode(next);
          setRaw("");
          setHeader(null);
          setIds(null);
        }}
        options={[
          { value: "letters", label: strings.modeLetters },
          { value: "scored", label: strings.modeScored },
        ]}
        value={mode}
      />
      <DataInput
        columnNames={names.slice(0, data.width)}
        example={
          mode === "letters" ? strings.exampleLetters : strings.exampleScored
        }
        fmt={fmt}
        id="ia-data"
        onChange={setRaw}
        onHeader={setHeader}
        onIds={setIds}
        onReset={reset}
        strings={inputStrings}
        summary={t(strings.readAs, {
          students: tPlural(strings.studentCount, locale, table.body.length, {
            count: fmt(table.body.length),
          }),
          items: tPlural(strings.itemCount, locale, data.width, {
            count: fmt(data.width),
          }),
        })}
        table={{ ...table, body: data.body }}
        value={raw}
      >
        {mode === "letters" ? (
          <KeyField
            itemKey={key}
            onChange={setKeyText}
            strings={strings}
            value={keyText}
          />
        ) : null}
      </DataInput>
      <PillGroup
        label={strings.groupLegend}
        name="ia-share"
        onChange={setShare}
        options={[
          { value: "0.27", label: strings.group27 },
          { value: "0.25", label: strings.group25 },
          { value: "0.33", label: strings.group33 },
          { value: "0.5", label: strings.group50 },
        ]}
        value={share}
      />

      <section aria-live="polite" className="space-y-6">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {needsKey ? <Notice>{strings.keyPrompt}</Notice> : null}
        {mismatch ? (
          <Notice>
            {t(strings.keyMismatch, {
              key: fmt(key.length),
              items: fmt(data.width),
            })}
          </Notice>
        ) : null}
        {analysis ? (
          <>
            <ScoringNotes
              blank={data.blank}
              fmt={fmt}
              invalid={data.invalid}
              strings={strings}
            />
            <TestSummary analysis={analysis} fmt={fmt} strings={strings} />
            <ItemTable
              analysis={analysis}
              fmt={fmt}
              itemKey={key}
              names={names}
              share={Number(share)}
              strings={strings}
            />
            {data.responses ? (
              <DistractorPanel
                analysis={analysis}
                fmt={fmt}
                itemKey={key}
                names={names}
                responses={data.responses}
                strings={strings}
              />
            ) : null}
          </>
        ) : null}
        {analysis || needsKey ? null : <Notice>{strings.enterPrompt}</Notice>}
      </section>
    </>
  );
}
